import React, { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { GetSocialAnalyticsPostsWithInsightsQuery, SocialPostType } from '@frontend/gql/social/graphql';

import { Button } from '@/shadcn/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shadcn/components/ui/card';
import { Skeleton } from '@/shadcn/components/ui/skeleton';

import { POST_TYPE_ORDER, getPostTypeColor, getPostTypeDisplayName } from '../../constants/postTypes';
import { formatShortNumber, getFormatStrategy } from '../../utils/format';

interface PostsBreakdownByMetricHistoricalCardProps {
  data: GetSocialAnalyticsPostsWithInsightsQuery['client']['socialAnalytics']['postsBreakdown'] | undefined;
  fetching: boolean;
}

type MetricType = 'impressions' | 'engagements';

type ChartDataPoint = {
  name: string;
  date: Date;
} & Partial<Record<SocialPostType, number>>;

const PostsBreakdownByMetricHistoricalCard: React.FC<PostsBreakdownByMetricHistoricalCardProps> = ({
  data,
  fetching,
}) => {
  const [activeMetric, setActiveMetric] = useState<MetricType>('impressions');

  const chartData = useMemo(() => {
    if (!data?.byPostType?.historical?.length) return [];

    // Group data by date period
    const groupedData: Record<string, ChartDataPoint> = {};

    data.byPostType.historical.forEach((item) => {
      const startDate = new Date(item.startDate);

      // Use dateResolution to format the period properly
      const formatStrategy = getFormatStrategy(item.dateResolution);
      const groupKey = formatStrategy(startDate);

      if (!groupedData[groupKey]) {
        groupedData[groupKey] = {
          name: groupKey,
          date: startDate,
        };
      }

      const postType = item.postType;
      const metricValue = activeMetric === 'impressions' ? item.impressions : item.engagements;
      groupedData[groupKey][postType] = (groupedData[groupKey][postType] || 0) + (metricValue || 0);
    });

    return Object.values(groupedData).sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [data, activeMetric]);

  const postTypeKeys = useMemo(() => {
    if (!chartData.length) return [];

    // Get all post types present in the data
    const presentPostTypes = Object.keys(chartData[0]).filter(
      (key) => key !== 'name' && key !== 'date',
    ) as SocialPostType[];

    // Order them according to POST_TYPE_ORDER
    return POST_TYPE_ORDER.filter((postType) => presentPostTypes.includes(postType));
  }, [chartData]);

  const METRIC_DISPLAY_NAMES: Record<MetricType, string> = {
    impressions: 'Impressions',
    engagements: 'Engagements',
  } as const;

  const getMetricDisplayName = (metric: MetricType) => METRIC_DISPLAY_NAMES[metric];

  if (fetching) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Metrics Over Time</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[400px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!chartData.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Metrics Over Time</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[300px] text-muted-foreground">No data available</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col h-full p-4 gap-4">
      <CardHeader className="flex p-0">
        <div className="flex justify-between items-start">
          <CardTitle>{getMetricDisplayName(activeMetric)} Over Time</CardTitle>
          <div className="flex gap-2 flex-shrink-0">
            <Button
              variant={activeMetric === 'impressions' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveMetric('impressions')}
              data-dd-action-name="social-analytics-impressions-toggle"
            >
              Impressions
            </Button>
            <Button
              variant={activeMetric === 'engagements' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveMetric('engagements')}
              data-dd-action-name="social-analytics-engagements-toggle"
            >
              Engagements
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="min-h-[400px] flex-1 p-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 24, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="name" tickLine={false} axisLine={false} tickMargin={10} tick={{ fontSize: 12 }} />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => formatShortNumber(value)}
            />
            <Tooltip formatter={(value, name) => [typeof value === 'number' ? value.toLocaleString() : value, name]} />
            {postTypeKeys.map((postType) => {
              const socialPostType = postType as SocialPostType;
              return (
                <Bar
                  key={postType}
                  dataKey={postType}
                  stackId="postType"
                  fill={getPostTypeColor(socialPostType)}
                  name={getPostTypeDisplayName(socialPostType)}
                />
              );
            })}
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
      <CardFooter className="flex flex-wrap items-center justify-center gap-4 p-0">
        {postTypeKeys.map((postType) => {
          const socialPostType = postType as SocialPostType;
          return (
            <div key={postType} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: getPostTypeColor(socialPostType) }} />
              <span className="text-xs text-muted-foreground">{getPostTypeDisplayName(socialPostType)}</span>
            </div>
          );
        })}
      </CardFooter>
    </Card>
  );
};

export default PostsBreakdownByMetricHistoricalCard;
